// frontend/src/components/CommandPalette.jsx
// Version: 1.0 (Sprint 3 - Global Executive Command Palette)
// Description: Глобальная командная строка (Cmd+K / Ctrl+K) в эстетике Quiet Luxury.
//              Быстрый переход по всем разделам практики Fitenko Law, живой поиск
//              по обращениям и делам, мгновенные действия Katifi AI.

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Search,
  Calendar,
  Briefcase,
  Layers,
  Bot,
  Scale,
  Star,
  Inbox,
  FileText,
  Users,
  Sparkles,
  ArrowRight,
  FolderPlus,
  CornerDownLeft,
  X,
  UserCheck
} from 'lucide-react';
import apiService, { razobratSpisokObrashcheniy } from '../services/apiService';
import './CommandPalette.css';

const STATIC_COMMANDS = [
  {
    id: 'cmd-katifi',
    title: 'Ask Katifi for priorities',
    subtitle: 'Summarize urgent deadlines, approvals, and AI swarm tasks',
    icon: Sparkles,
    category: 'Quick Actions',
    action: (navigate) => {
      window.dispatchEvent(new CustomEvent('open-katifi-chat', {
        detail: { prompt: 'Summarize what the AI team needs me to decide today' }
      }));
      toast('Katifi AI: формирую сводку приоритетов практики...', { icon: '✨' });
    }
  },
  {
    id: 'cmd-new-case',
    title: 'Create New Case File',
    subtitle: 'Start EB-1A, E-2, NIW or Family Adjustment dossier',
    icon: FolderPlus,
    category: 'Quick Actions',
    action: (navigate) => navigate('/cases/new')
  },
  {
    id: 'nav-calendar',
    title: 'Calendar & Consultations',
    subtitle: 'Google Calendar sync, meeting rooms & bookings',
    icon: Calendar,
    category: 'Navigation',
    action: (navigate) => navigate('/calendar')
  },
  {
    id: 'nav-cases',
    title: 'Active Cases & USCIS Vault',
    subtitle: 'Client dossiers, strategy memos, and checklists',
    icon: Briefcase,
    category: 'Navigation',
    action: (navigate) => navigate('/cases')
  },
  {
    id: 'nav-ai-ops',
    title: 'AI Operations Hub',
    subtitle: 'Sofia Voice transcripts, Katifi notes, and live AI Swarm',
    icon: Layers,
    category: 'Navigation',
    action: (navigate) => navigate('/ai-ops')
  },
  {
    id: 'nav-paralegal',
    title: 'AI Paralegal (Elena)',
    subtitle: 'Evidence gap analysis and legal risk council',
    icon: Scale,
    category: 'Navigation',
    action: (navigate) => navigate('/ai-paralegal')
  },
  {
    id: 'nav-reputation',
    title: 'Reputation & Google Maps',
    subtitle: 'Coral Springs canon, 5.0 Google reviews, and SEO ranking',
    icon: Star,
    category: 'Navigation',
    action: (navigate) => navigate('/reputation')
  },
  {
    id: 'nav-inbox',
    title: 'Omnichannel Inbox',
    subtitle: 'Incoming phone calls, WhatsApp, SMS, and web chat',
    icon: Inbox,
    category: 'Navigation',
    action: (navigate) => navigate('/inbox')
  },
  {
    id: 'nav-leads',
    title: 'Leads & Intake Pipeline',
    subtitle: 'Prospects from Sofia calls, 1-click Case converter',
    icon: Users,
    category: 'Navigation',
    action: (navigate) => navigate('/leads')
  },
  {
    id: 'nav-doc-gen',
    title: 'Document Generator',
    subtitle: 'Legal petition drafter, affidavits, and executive templates',
    icon: FileText,
    category: 'Navigation',
    action: (navigate) => navigate('/document-generator')
  },
  {
    id: 'nav-ai-staff',
    title: 'AI Swarm Roster',
    subtitle: '9 active DB agents, XP compute scores, and departments',
    icon: Bot,
    category: 'Navigation',
    action: (navigate) => navigate('/ai-staff')
  }
];

export default function CommandPalette() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [liveLeads, setLiveLeads] = useState([]);
  const [liveCases, setLiveCases] = useState([]);
  const inputRef = useRef(null);

  // Global hotkey: Cmd+K / Ctrl+K or custom trigger
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      } else if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    const handleCustomOpen = () => setIsOpen(true);

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('open-command-palette', handleCustomOpen);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('open-command-palette', handleCustomOpen);
    };
  }, [isOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);

      // Preload recent leads and cases in background
      apiService.getLeads()
        .then((data) => {
          const parsed = razobratSpisokObrashcheniy(data);
          if (parsed.rod === 'uspeh') {
            setLiveLeads(parsed.items || []);
          }
        })
        .catch(() => {});

      apiService.getCases()
        .then((data) => {
          setLiveCases(Array.isArray(data) ? data : []);
        })
        .catch(() => {});
    }
  }, [isOpen]);

  // Filtered results
  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();

    if (!q) {
      return STATIC_COMMANDS;
    }

    const matchedStatic = STATIC_COMMANDS.filter(
      (c) =>
        c.title.toLowerCase().includes(q) ||
        c.subtitle.toLowerCase().includes(q) ||
        c.category.toLowerCase().includes(q)
    );

    const matchedLeads = liveLeads
      .filter(
        (l) =>
          l.full_name?.toLowerCase().includes(q) ||
          l.email?.toLowerCase().includes(q) ||
          l.phone?.toLowerCase().includes(q)
      )
      .slice(0, 5)
      .map((l) => ({
        id: `lead-${l.id}`,
        title: l.full_name || 'Prospect',
        subtitle: `${l.phone || l.email || 'Lead'} · Status: ${l.status}`,
        icon: UserCheck,
        category: 'Leads & Intake',
        action: (nav) => nav(`/history?lead=${l.id}`)
      }));

    const matchedCases = liveCases
      .filter(
        (c) =>
          c.title?.toLowerCase().includes(q) ||
          c.case_type?.toLowerCase().includes(q) ||
          String(c.id).includes(q)
      )
      .slice(0, 5)
      .map((c) => ({
        id: `case-${c.id}`,
        title: c.title || `Case #${c.id}`,
        subtitle: `${c.case_type || 'General'} · Status: ${c.status || 'Active'}`,
        icon: Briefcase,
        category: 'Active Cases',
        action: (nav) => nav(`/cases/${c.id}`)
      }));

    return [...matchedStatic, ...matchedCases, ...matchedLeads];
  }, [query, liveLeads, liveCases]);

  // Adjust activeIndex if filtered items length changes
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Keyboard navigation within results
  const handleInputKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev < filteredItems.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev > 0 ? prev - 1 : filteredItems.length - 1
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = filteredItems[activeIndex];
      if (item) {
        setIsOpen(false);
        item.action(navigate);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="cmd-palette-backdrop"
      onClick={() => setIsOpen(false)}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="cmd-palette-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Header */}
        <div className="cmd-palette-search-bar">
          <Search className="cmd-palette-icon" size={18} strokeWidth={2} />
          <input
            ref={inputRef}
            type="text"
            className="cmd-palette-input"
            placeholder="Search cases, leads, AI agents or type a command..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleInputKeyDown}
          />
          <span className="cmd-palette-badge">ESC</span>
        </div>

        {/* Results List */}
        <div className="cmd-palette-results">
          {filteredItems.length === 0 ? (
            <div style={{ padding: '24px 16px', textAlign: 'center', color: '#728272', fontSize: '13px' }}>
              No matching actions, cases or leads found.
            </div>
          ) : (
            filteredItems.map((item, index) => {
              const IconComp = item.icon;
              const isSelected = index === activeIndex;

              return (
                <div
                  key={item.id}
                  className={`cmd-palette-item ${isSelected ? 'active' : ''}`}
                  onClick={() => {
                    setIsOpen(false);
                    item.action(navigate);
                  }}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  <div className="cmd-palette-item-left">
                    <div className="cmd-palette-item-icon-box">
                      <IconComp size={16} />
                    </div>
                    <div className="cmd-palette-item-text">
                      <span className="cmd-palette-item-title">{item.title}</span>
                      <span className="cmd-palette-item-subtitle">{item.subtitle}</span>
                    </div>
                  </div>

                  <div className="cmd-palette-item-right">
                    {isSelected && (
                      <span className="cmd-palette-key-hint">
                        <CornerDownLeft size={10} /> Jump
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer info */}
        <div className="cmd-palette-footer">
          <div className="cmd-palette-footer-shortcuts">
            <span><kbd className="cmd-palette-footer-kbd">↑↓</kbd> navigate</span>
            <span><kbd className="cmd-palette-footer-kbd">↵</kbd> select</span>
            <span><kbd className="cmd-palette-footer-kbd">esc</kbd> close</span>
          </div>
          <span style={{ color: '#C8A653', fontWeight: '600' }}>Fitenko Law OS</span>
        </div>
      </div>
    </div>
  );
}
